import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { StyleSheet, View } from "react-native";
import { dataConstants } from "services/redux/data/constants";
import { Column, Container, Grid, Row } from 'components/atoms/BootstrapGrid'
import Translate from "utils/translate";
import ThemedBlock from "components/atoms/ThemedBlock";
import _ from "lodash";
import ThemedBlockTitle from 'components/atoms/ThemedBlockTitle'
import { constantsActive } from "utils/constants/constants";
import { roomConstants } from 'utils/constants/room-constants'
import ThemedList from 'components/atoms/ThemedList'
import ManagementDashboardComponentMeetingRoomsTimeslots from "./buildingMeetingRoomsTimeslots"
import ManagementDashboardModalMeetingRoomDetails from "../modals/buildingMeetingRoomDetails";
import * as Progress from 'react-native-progress';
import FetchTimeslotCapacity from "utils/fetch/FetchTimeslotCapacity";
import i18n from "utils/i18n";
import { colorList } from "utils/colors";
import Text from "theme/Text";
import ThemedPressable from 'components/atoms/ThemedPressable'
import ReactTimeout from 'react-timeout'
import FetchManagementTimeslotCapacity from 'utils/fetch/management/FetchManagementTimeslotCapacity'
import HelperDate, { dateFormat } from 'utils/helper-date'

const ManagementDashboardComponentMeetingRooms = ( { building = null, setTimeout, clearTimeout } ) => {
	
	const reloadTimeout = useRef()
	
	const [ showAllTimeslots, setShowAllTimeslots ] = useState( false );
	const [ meetingRooms, setMeetingRooms ] = useState( [] );
	const [ roomTimeslotBookings, setRoomTimeslotBookings ] = useState( {} );
	const [ roomTimeslotBookingLoading, setRoomTimeslotBookingLoading ] = useState( false );
	const [ roomTimeslotModalData, setRoomTimeslotModalData ] = useState( null );

	const _floors = useSelector( state => {
		return state.data[dataConstants.FLOORS];
	} )
	const _rooms = useSelector( state => {
		return state.data[dataConstants.ROOMS];
	} )

	const reloadInterval = 30*1000;
	
	const fetchCapacity = () => {
		FetchManagementTimeslotCapacity.fetch( {
			room: meetingRooms,
			dates: [
				HelperDate.date().format( dateFormat.DATE )
			],
			callbackStart: setRoomTimeslotBookingLoading.bind( this, true ),
			callbackError: () => {
				setRoomTimeslotBookingLoading( false )
				setReloadTimeout()
			},
			callbackSuccess: ( result ) => {
				_.forEach( result, (value, key) => {
					const guids = FetchTimeslotCapacity.extractKeys( key );
					
					if ( _.get( roomTimeslotBookings, guids.room, null ) === null ) {
						roomTimeslotBookings[ guids.room ] = {};
					}
					
					if ( _.get( roomTimeslotBookings, [ guids.room, guids.timeslot ], null ) === null ) {
						roomTimeslotBookings[ guids.room ][ guids.timeslot ] = value;
					}
					
					setReloadTimeout()
				})
				
				setRoomTimeslotBookings({
					...roomTimeslotBookings
				});
				
				setRoomTimeslotBookingLoading( false );
			},
		} );
	}
	
	const setReloadTimeout = () => {
		clearTimeout( reloadTimeout.current )
		
		reloadTimeout.current = setTimeout( fetchCapacity, reloadInterval )
	}

	useEffect( () => {
		// fetch all the meeting rooms
		const tmpMeetingRooms = [];
		
		const timeSlotPast = HelperDate.date().subtract(1.5, 'hours').format( 'HH:mm' );
		const timeSlotFuture = HelperDate.date().add(2.5, 'hours').format( 'HH:mm' );
		const currentDayOfWeek = HelperDate.date().day();
		
		_.get( building, 'floors', [] ).forEach( buildingFloor => {
			const rooms = _.get( _floors, [ buildingFloor.guid, 'rooms' ], [] );

			rooms.forEach( floorRoom => {
				const room = _.get( _rooms, floorRoom.guid, null );

				if ( room !== null && room.active === constantsActive.YES && room?.kind === roomConstants.KIND_MEETING_ROOM ) {
					
					// Get the timeslots for the room
					// -> filter by the timeslot_day and order by time
					const tmpAllTimeSlots = _.orderBy( _.filter( _.get( room, 'timeslots_through', [] ), { timeslot_day: currentDayOfWeek } ), ['timeslot_start'],['asc'] );
					
					let tmpFilteredTimeSlots = tmpAllTimeSlots;
					if( showAllTimeslots === false ) {
						tmpFilteredTimeSlots = _.filter( tmpAllTimeSlots, ( timeslot ) => {
							return timeSlotPast <= timeslot.timeslot_end.substr( 0, 5 ) && timeSlotFuture >= timeslot.timeslot_end.substr( 0, 5 )
						} )
					}

					if( tmpFilteredTimeSlots.length > 0 ) {
						tmpMeetingRooms.push( {
							...room,
							timeslots: tmpFilteredTimeSlots,
							timeslots_through: tmpFilteredTimeSlots
						});
					}
				}
			} )
		});

		setMeetingRooms( tmpMeetingRooms );
	}, [ building, showAllTimeslots ] );

	useEffect( () => {
		return fetchCapacity();
	}, [ meetingRooms ] );

	let roomTimeslotModal = null;
	if( roomTimeslotModalData !== null ) {
		roomTimeslotModal = (
			<ManagementDashboardModalMeetingRoomDetails
				onClose={ setRoomTimeslotModalData.bind( this, null ) }
				building={ building }
				room={ roomTimeslotModalData.room }
				timeslot={ roomTimeslotModalData.timeslot }
				timeslots={  _.get( roomTimeslotModalData.room , 'all_timeslots', [] ) }
				bookings={ _.get( roomTimeslotBookings, roomTimeslotModalData.room.guid, null ) }
			/>
		);
	}

	if( meetingRooms.length === 0 ) {
		return null;
	}
	
	return (
		<View>
			{ roomTimeslotModal }
			
			<Grid>
				<Container>
					<Row style={ styles.rowMarginBottom6 } justify={'flex-start'} align={'center'}>
						<Column>
							<ThemedBlockTitle smallPadding style={ styles.themedBlockTitlePaddingBottom0 }>
								{ i18n.t( 'management:pages.dashboard.titles.meeting_rooms', { name: Translate.field( building, 'title' ) } ) }
							</ThemedBlockTitle>
						</Column>
						<Column>
							<ThemedPressable onPress={ setShowAllTimeslots.bind( this, !showAllTimeslots ) }>
								<Text style={ styles.textShowAllTimeSlots }>
									{ !showAllTimeslots ? i18n.t( 'management:pages.dashboard.meeting_rooms.show_all_timeslots' ) : i18n.t( 'management:pages.dashboard.meeting_rooms.hide_all_timeslots' ) }
								</Text>
							</ThemedPressable>
						</Column>
					</Row>
					
					<Row innerSpan={{ xs: 12, md: 4 }}>
						{ _.map( meetingRooms, ( meetingRoom, key ) => {
							return (
								<Column>
									<ThemedBlock
										shadow
										header={ i18n.t( 'management:pages.dashboard.meeting_rooms.title', {name: Translate.field( meetingRoom, 'title' )} ) }>

										<View style={ styles.viewHeight12MarginBottom4 }>
											{ roomTimeslotBookingLoading !== false && (
												<Progress.Bar
													indeterminate={ true }
													height={4}
													width={ null }
													animated={ true }
													color={ colorList.ORANGE.light }
													borderRadius={0}
													borderWidth={0}
												/>
											)}
										</View>

										<ThemedList
											data={ _.get( meetingRoom, 'timeslots', [] ) }
											dividerEnabled={true}
											renderItem={( {item = [] } ) => {
												return (
													<ManagementDashboardComponentMeetingRoomsTimeslots
														onPress={ setRoomTimeslotModalData.bind( this, { room: meetingRoom, timeslot: item }) }
														timeslot={ item }
														bookings={ _.get( roomTimeslotBookings, [ meetingRoom.guid, item.guid ], null ) }
													/>
												);
											}}
										/>
									</ThemedBlock>
								</Column>
							);
						})}
					</Row>
				</Container>
			</Grid>
		</View>
	);
}

export const styles = StyleSheet.create({
	rowMarginBottom6: {
		marginBottom: 6,
	},
	themedBlockTitlePaddingBottom0: {
		paddingBottom: 0,
	},
	viewHeight12MarginBottom4: {
		height: 12,
		marginBottom: 4,
		marginHorizontal: -3,
	},
	textShowAllTimeSlots: {
		color: colorList.ORANGE.primary,
		textDecorationLine: 'underline',
		marginLeft: 10
	},
});

export default ReactTimeout( ManagementDashboardComponentMeetingRooms )
